import { useEffect, useMemo, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { type SchoolLevel } from '@jeraisonne/db';
import { Card } from '@jeraisonne/ui';
import {
  ArrowRight,
  CalendarClock,
  Clock,
  FlaskConical,
  Gift,
  ListChecks,
  Trash2,
  Trophy,
  Users,
} from 'lucide-react';
import { AvatarCircle } from '@/components/avatar-circle';
import { LivePrizeTile } from '@/features/live-quiz/live-prize-tile';
import { liveCoverUrl, presetPath } from '@/lib/live-covers';
import {
  GlassChip,
  LiveCardMeta,
  LiveCoverCard,
  liveCtaClass,
} from '@/features/live-ui/live-cover-card';
import { levelSubjectChip, type LiveCardBadge } from '@/features/live-ui/live-card-model';
import { useAuth } from '@/lib/auth';
import { isLiveTester } from '@/lib/live-tester';
import { useMyTestChallengeIds, useDeleteTestParticipation } from '@/lib/live-test';
import {
  registeredLabel,
  useLiveRegistrationCounts,
  useMyLiveRegistrations,
  useLiveRegistrationMutations,
} from '@/lib/live-registrations';
import { syncLiveNotifications } from '@/lib/live-local-notifications';
import { prizePlaceLabel, useMyLivePrizeChoices } from '@/lib/live-prize-choice';
import { useLiveEvents } from './use-live-events';
import {
  partitionLiveEvents,
  pickHeroCampaign,
  formatLiveEventDateTime,
  formatLiveEventTime,
  groupLiveEventsByDay,
  liveEventDurationMin,
  liveEventProgrammeLabel,
  type LiveCampaignCover,
  type LiveEvent,
  type LiveEventPhase,
  type LivePrizeCards,
  type LivePrizeWinner,
} from './live-events';

const EMPTY_IDS: ReadonlySet<string> = new Set<string>();

/**
 * Onglet « Live School » : le Live School du dimanche (migration 0329) — un live
 * de 10 min par programme chaque dimanche dès 10 h, les uns après les autres,
 * avec une SKILLCARD à gagner pour le podium. Puis les lives du niveau (En
 * cours / À venir regroupés par jour / Passés avec le podium). En mode élève
 * (`canJoin`), « Je participe » programme un rappel email 10 min avant + notif
 * locale mobile 1 min avant. Tout le monde peut jouer (`canPlay`) ; seuls les
 * élèves du niveau sont classés.
 */
export function LiveSchoolTab({
  level,
  userId,
  canJoin,
  canPlay = canJoin,
  onSeeRanking,
}: {
  level: SchoolLevel | null;
  userId: string;
  /** Élève du niveau : inscription + rappels, joue classé. */
  canJoin: boolean;
  /** Peut jouer (sans classement pour prof / curieux / parent). */
  canPlay?: boolean;
  onSeeRanking: (challengeId: string) => void;
}) {
  const { data, isLoading, isError } = useLiveEvents(level);
  const { data: registeredIds = EMPTY_IDS } = useMyLiveRegistrations(canJoin ? userId : '');
  const { data: counts } = useLiveRegistrationCounts((data?.upcoming ?? []).map((e) => e.id));
  const { registerMany, unregisterMany, isMutating } = useLiveRegistrationMutations(userId);
  // Lots à choisir (podium d'un live dont il possédait déjà la SKILLCARD, 0331).
  const { data: prizeChoices = [] } = useMyLivePrizeChoices(userId, canJoin);

  // Testeurs (staff/dev) : bouton « Tester ce défi » + suppression de la
  // participation de test. `import.meta.env.DEV` couvre le localhost.
  const { profile, user } = useAuth();
  const tester = isLiveTester(profile, user?.email);
  const { data: testIds = EMPTY_IDS } = useMyTestChallengeIds(tester ? userId : '');
  const deleteTest = useDeleteTestParticipation(userId);

  const buckets = useMemo(() => {
    if (!data) return null;
    return partitionLiveEvents([...data.upcoming, ...data.past], data.server_time);
  }, [data]);

  const hero = useMemo<LiveCampaignCover | null>(() => {
    if (!data || !buckets) return null;
    return pickHeroCampaign(buckets, data.campaigns ?? []);
  }, [data, buckets]);

  // Réconcilie les notifications locales mobiles (1 min avant) avec l'état
  // d'inscription courant. No-op sur le web ; best-effort côté natif.
  useEffect(() => {
    if (!canJoin || !buckets) return;
    void syncLiveNotifications(buckets.upcoming, registeredIds);
  }, [canJoin, buckets, registeredIds]);

  if (!level) {
    return (
      <p className="text-ink-600 py-8 text-center text-sm">
        Choisis un niveau pour voir les défis du Live School.
      </p>
    );
  }
  if (isLoading) {
    return <p className="text-ink-600 py-8 text-center text-sm">Chargement des défis…</p>;
  }
  if (isError || !buckets) {
    return (
      <p className="text-ink-600 py-8 text-center text-sm">
        Impossible de charger les défis pour le moment.
      </p>
    );
  }

  const prizes = data?.prizes ?? {};
  const winners = data?.winners ?? {};
  const total = buckets.live.length + buckets.upcoming.length + buckets.finished.length;
  if (total === 0) {
    return (
      <>
        {hero ? <CampaignHero campaign={hero} /> : <SundayIntro />}
        <p className="text-ink-600 py-8 text-center text-sm">
          Aucun défi programmé pour ce niveau.
        </p>
      </>
    );
  }

  const upcomingIds = buckets.upcoming.map((e) => e.id);
  const allUpcomingRegistered =
    upcomingIds.length > 0 && upcomingIds.every((id) => registeredIds.has(id));

  // Props « testeur » (staff/dev) mutualisées pour chaque carte.
  const testerProps = (id: string): TesterCardProps =>
    tester
      ? {
          tester: true,
          hasTest: testIds.has(id),
          deleteBusy: deleteTest.isPending,
          onDeleteTest: () => deleteTest.mutate(id),
        }
      : {};

  const campaignCovers = new Map(
    (data?.campaigns ?? []).filter((c) => c.cover_url).map((c) => [c.id, c.cover_url]),
  );
  const cardProps = (e: LiveEvent) => ({
    campaignCoverUrl: e.campaign_id ? (campaignCovers.get(e.campaign_id) ?? null) : null,
    prizeCards: prizes,
    winners: winners[e.id] ?? [],
    canPlay,
    ranked: canJoin,
    registeredCount: counts?.get(e.id),
  });

  return (
    <div className="space-y-6">
      {prizeChoices.map((c) => (
        <Link
          key={c.challenge_id}
          to="/lot/$challengeId"
          params={{ challengeId: c.challenge_id }}
          className="border-accent-300 bg-accent-50 ocean:bg-accent-500/10 flex min-h-11 items-center gap-3 rounded-2xl border p-3 active:scale-[0.98]"
        >
          <Gift className="text-accent-600 h-6 w-6 shrink-0" aria-hidden />
          <span className="text-sm">
            <span className="text-ink-900 block font-semibold">
              {prizePlaceLabel(c.rank)} : choisis ta SKILLCARD
            </span>
            <span className="text-ink-600 block">
              Tu avais déjà le lot de {c.title ?? 'ce live'}, prends celle que tu veux.
            </span>
          </span>
        </Link>
      ))}
      {hero ? <CampaignHero campaign={hero} /> : <SundayIntro />}

      {canJoin && upcomingIds.length > 0 && (
        <Card className="p-3.5">
          <ParticipateSwitchRow
            label="Participer à tous les défis"
            hint="Un rappel avant chaque défi à venir de ton niveau."
            checked={allUpcomingRegistered}
            disabled={isMutating}
            onChange={(on) => (on ? registerMany(upcomingIds) : unregisterMany(upcomingIds))}
          />
        </Card>
      )}

      {buckets.live.length > 0 && (
        <Section title="En cours">
          {buckets.live.map((e) => (
            <LiveEventCard
              key={e.id}
              event={e}
              phase="live"
              level={level}
              canJoin={canJoin}
              onSeeRanking={onSeeRanking}
              {...cardProps(e)}
              {...testerProps(e.id)}
            />
          ))}
        </Section>
      )}
      {groupLiveEventsByDay(buckets.upcoming).map((g) => (
        <Section key={g.day} title={`À venir · ${g.day}`}>
          {g.events.map((e) => (
            <LiveEventCard
              key={e.id}
              event={e}
              phase="upcoming"
              level={level}
              canJoin={canJoin}
              onSeeRanking={onSeeRanking}
              registered={registeredIds.has(e.id)}
              registerBusy={isMutating}
              onToggleRegister={() =>
                registeredIds.has(e.id) ? unregisterMany([e.id]) : registerMany([e.id])
              }
              {...cardProps(e)}
              {...testerProps(e.id)}
            />
          ))}
        </Section>
      ))}
      {buckets.finished.length > 0 && (
        <Section title="Passés">
          {buckets.finished.map((e) => (
            <LiveEventCard
              key={e.id}
              event={e}
              phase="finished"
              level={level}
              canJoin={false}
              onSeeRanking={onSeeRanking}
              {...cardProps(e)}
              {...testerProps(e.id)}
            />
          ))}
        </Section>
      )}
    </div>
  );
}

/** Présentation du Live School du dimanche (sans cover de campagne). */
function SundayIntro() {
  return (
    <LiveCoverCard
      size="md"
      coverUrl={liveCoverUrl({ path: presetPath('arene') })}
      chip={
        <>
          <CalendarClock className="h-3.5 w-3.5" aria-hidden /> Chaque dimanche dès 10 h
        </>
      }
      title="Live School du dimanche"
    >
      <p className="mt-1.5 text-sm text-white/85">
        Un live de 10 minutes par programme, les uns après les autres. Chaque bonne réponse te
        rapporte de l’HORA, d’autant plus que tu réponds vite. Le podium gagne une SKILLCARD.
      </p>
    </LiveCoverCard>
  );
}

function CampaignHero({ campaign }: { campaign: LiveCampaignCover }) {
  if (!campaign.cover_url) return null;
  return (
    <LiveCoverCard size="md" coverUrl={campaign.cover_url} title={campaign.title}>
      {campaign.subtitle && <p className="mt-1.5 text-sm text-white/85">{campaign.subtitle}</p>}
    </LiveCoverCard>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="font-display text-ink-500 mb-2 text-xs font-bold uppercase tracking-widest">
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/** Props « testeur » (staff/dev) : bouton Tester + suppression participation test. */
interface TesterCardProps {
  tester?: boolean;
  hasTest?: boolean;
  deleteBusy?: boolean;
  onDeleteTest?: () => void;
}

const PHASE_BADGE: Record<LiveEventPhase, LiveCardBadge> = {
  live: 'live',
  upcoming: 'upcoming',
  finished: 'finished',
};

function LiveEventCard({
  event,
  phase,
  level,
  canJoin,
  onSeeRanking,
  registered = false,
  registerBusy = false,
  onToggleRegister,
  tester = false,
  hasTest = false,
  deleteBusy = false,
  onDeleteTest,
  prizeCards = {},
  winners = [],
  canPlay = canJoin,
  ranked = canJoin,
  campaignCoverUrl = null,
  registeredCount,
}: {
  event: LiveEvent;
  phase: LiveEventPhase;
  level: SchoolLevel;
  canJoin: boolean;
  onSeeRanking: (challengeId: string) => void;
  registered?: boolean;
  registerBusy?: boolean;
  onToggleRegister?: () => void;
  prizeCards?: LivePrizeCards;
  winners?: LivePrizeWinner[];
  canPlay?: boolean;
  ranked?: boolean;
  /** Cover de la campagne du live, sinon un preset tiré du live. */
  campaignCoverUrl?: string | null;
  /** Nombre d'inscrits (« Je participe »), migration 0733. */
  registeredCount?: number;
} & TesterCardProps) {
  const isProgramme = !!event.programme_id;
  const prize = event.prize ? prizeCards[event.prize.card_id] : null;
  const playSearch = isProgramme ? { defi: event.id } : undefined;
  const when =
    phase === 'upcoming' && isProgramme
      ? formatLiveEventTime(event.starts_at)
      : formatLiveEventDateTime(event.starts_at);
  const meta = [
    { icon: ListChecks, text: `${event.step_count} questions` },
    ...(isProgramme ? [{ icon: Clock, text: `${liveEventDurationMin(event)} min` }] : []),
    ...(event.scoring === 'hora' ? [{ icon: Trophy, text: 'Classement en HORA' }] : []),
    ...(phase !== 'finished' && registeredCount !== undefined
      ? [{ icon: Users, text: registeredLabel(registeredCount) }]
      : []),
  ];
  return (
    <LiveCoverCard
      size={phase === 'live' ? 'lg' : 'md'}
      coverUrl={liveCoverUrl({ fallbackUrl: campaignCoverUrl, seed: event.id })}
      badge={PHASE_BADGE[phase]}
      chip={levelSubjectChip(event.level, event.subject)}
      corner={
        <div className="flex flex-col items-end gap-1">
          <GlassChip>{when}</GlassChip>
          {event.special && (
            <span className="rounded-full bg-[#f4c64e] px-2.5 py-1 text-xs font-bold text-[#0a2e57]">
              Spécial vacances
            </span>
          )}
        </div>
      }
      title={isProgramme ? liveEventProgrammeLabel(event) : (event.title ?? 'Défi National')}
    >
      <LiveCardMeta className="mt-2" items={meta} />

      {event.prize && prize && (
        <LivePrizeTile
          prize={{ ...prize, card_id: event.prize.card_id, top_n: event.prize.top_n }}
          compact
          tone="glass"
          className="mt-3"
        />
      )}

      {phase === 'finished' && winners.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2" aria-label="Gagnants du lot">
          {winners.map((w) => (
            <li
              key={w.student_id}
              className="flex items-center gap-1.5 rounded-full bg-black/30 py-1 pl-1 pr-2.5 text-xs text-white ring-1 ring-[rgba(255,255,255,0.2)] backdrop-blur"
            >
              <AvatarCircle name={w.display_name ?? '?'} src={w.avatar_url} size="xs" />
              <span className="font-semibold">
                {w.rank === 1 ? '🥇' : w.rank === 2 ? '🥈' : w.rank === 3 ? '🥉' : `${w.rank}e`}{' '}
                {(w.display_name ?? 'Élève').split(' ')[0]}
              </span>
            </li>
          ))}
        </ul>
      )}

      {phase === 'live' && canPlay && (
        <Link
          to="/live/$niveau"
          params={{ niveau: level }}
          search={playSearch}
          className={liveCtaClass('primary', 'mt-4')}
        >
          {ranked ? 'Rejoindre le live' : 'Jouer sans classement'}
          <ArrowRight className="h-5 w-5" aria-hidden />
        </Link>
      )}
      {phase === 'upcoming' && canJoin && (
        <div className="mt-3 space-y-1 rounded-2xl bg-black/30 p-3 ring-1 ring-[rgba(255,255,255,0.15)] backdrop-blur">
          <ParticipateSwitchRow
            tone="glass"
            label={registered ? 'Tu participes ✓' : 'Je participe'}
            hint={
              registered
                ? "Rappel email 10 min avant, notif 1 min avant sur l'app mobile."
                : "Sois prévenu·e avant le coup d'envoi."
            }
            checked={registered}
            disabled={registerBusy}
            onChange={() => onToggleRegister?.()}
          />
          <Link
            to="/live/$niveau"
            params={{ niveau: level }}
            search={playSearch}
            className="inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-[#f4c64e]"
          >
            Ouvrir la salle <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      )}
      {phase === 'upcoming' && !canJoin && canPlay && (
        <Link
          to="/live/$niveau"
          params={{ niveau: level }}
          search={playSearch}
          className={liveCtaClass('glass', 'mt-4')}
        >
          Ouvrir la salle (sans classement)
        </Link>
      )}
      {phase === 'finished' && (
        <button
          type="button"
          onClick={() => onSeeRanking(event.id)}
          className={liveCtaClass('glass', 'mt-4')}
        >
          <Trophy className="h-5 w-5" aria-hidden />
          Voir le classement
        </button>
      )}

      {tester && (
        <div className="mt-3 flex flex-col gap-1 border-t border-[rgba(255,255,255,0.15)] pt-2 sm:flex-row sm:items-center sm:gap-4">
          <a
            href={`/live/${event.level}?test=${event.id}`}
            className="inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-[#f4c64e]"
          >
            <FlaskConical className="h-4 w-4" />
            Tester ce défi
          </a>
          {hasTest && (
            <button
              type="button"
              disabled={deleteBusy}
              onClick={onDeleteTest}
              className="inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-[#fca5a5] disabled:opacity-60"
            >
              <Trash2 className="h-4 w-4" />
              {deleteBusy ? 'Suppression…' : 'Supprimer ma participation de test'}
            </button>
          )}
        </div>
      )}
    </LiveCoverCard>
  );
}

/** Ligne « label + interrupteur » d'inscription (calque du switch app, accessible). */
export function ParticipateSwitchRow({
  label,
  hint,
  checked,
  disabled,
  onChange,
  tone = 'default',
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
  /** `glass` : posé sur une cover (texte blanc). */
  tone?: 'default' | 'glass';
}) {
  return (
    <label
      className={`flex items-center justify-between gap-3 ${
        disabled ? 'opacity-60' : 'cursor-pointer'
      }`}
    >
      <span className="min-w-0">
        <span
          className={`block text-sm font-semibold ${tone === 'glass' ? 'text-white' : 'text-ink-900'}`}
        >
          {label}
        </span>
        <span
          className={`mt-0.5 block text-xs ${tone === 'glass' ? 'text-white/70' : 'text-ink-400'}`}
        >
          {hint}
        </span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`relative inline-flex h-7 w-12 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none ${
          checked ? 'bg-accent-500' : 'bg-ink-200'
        } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </label>
  );
}
